import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lightning, Building, ArrowsClockwise, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { inviteApi } from "@/lib/api";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [state, setState] = useState({ loading: true, error: null, invite: null });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing invite token", invite: null });
      return;
    }
    inviteApi.preview(token)
      .then((inv) => setState({ loading: false, error: null, invite: inv }))
      .catch((e) => setState({
        loading: false,
        error: e.response?.data?.detail || "Invite is invalid or expired",
        invite: null,
      }));
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    setSubmitting(true);
    try {
      await inviteApi.accept({ token, name: name.trim() || undefined, password });
      toast.success(`Welcome to ${state.invite.company_name}!`);
      window.location.replace("/live");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept invite");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4" data-testid="accept-invite-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[#ff8c00]">
            <Lightning size={28} weight="fill" />
            <span className="font-heading font-black text-2xl tracking-tight">LOAD LOGIC</span>
          </div>
        </div>

        <div className="bg-[#0d0d0d] border border-[#2a2a2a]">
          <div className="border-b border-[#2a2a2a] px-6 py-5">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-1">Invitation</div>
            <h1 className="font-heading font-black text-xl tracking-tight">Join the dispatch team</h1>
          </div>

          <div className="p-6">
            {state.loading && (
              <div className="flex items-center justify-center gap-2 py-8 text-[#71717a] text-xs uppercase tracking-[0.2em]">
                <ArrowsClockwise size={14} className="animate-spin" /> Validating invite…
              </div>
            )}

            {state.error && (
              <div className="text-center py-6" data-testid="invite-error">
                <Warning size={36} weight="fill" className="text-[#ef4444] mx-auto mb-3" />
                <div className="text-sm font-bold text-[#ef4444] mb-2">Invite unavailable</div>
                <div className="text-xs text-[#a1a1aa] mb-5 leading-relaxed">{state.error}</div>
                <Link to="/login" className="inline-block px-4 py-2 border border-[#ff8c00] text-[#ff8c00] hover:bg-[#ff8c00] hover:text-black text-[10px] uppercase tracking-[0.2em] font-bold">
                  Go to Login
                </Link>
              </div>
            )}

            {state.invite && (
              <>
                <div className="mb-5 p-3 bg-[#1a1a1a] border-l-2 border-[#ff8c00]" data-testid="invite-summary">
                  <div className="flex items-center gap-2 mb-1">
                    <Building size={14} className="text-[#4682b4]" />
                    <div className="font-heading font-bold text-sm">{state.invite.company_name}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">Role</div>
                      <div className="text-xs font-mono text-[#ff8c00] uppercase">{state.invite.role}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#71717a]">Email</div>
                      <div className="text-xs font-mono text-[#a1a1aa] truncate">{state.invite.email}</div>
                    </div>
                  </div>
                </div>

                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Display name (optional)</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                      data-testid="accept-name"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717a] mb-1">Set password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                      className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none text-sm"
                      data-testid="accept-password"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 bg-[#ff8c00] text-black hover:bg-[#e67e00] disabled:opacity-50 text-[10px] uppercase tracking-[0.2em] font-bold flex items-center justify-center gap-2"
                    data-testid="accept-submit"
                  >
                    {submitting ? <ArrowsClockwise size={12} className="animate-spin" /> : <CheckCircle size={12} weight="fill" />}
                    Accept & Sign In
                  </button>
                </form>
              </>
            )}
          </div>
        </div>

        <div className="text-center mt-4">
          <Link to="/login" className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] hover:text-[#ff8c00]">Already have an account? Sign in</Link>
        </div>
      </div>
    </div>
  );
}
